import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, setPartnerToken } from "../../lib/partnerApi";

export default function PartnerLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (params.get("expired")) {
      toast.error("Session expired — please sign in again.");
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await partnerApi.post("/partner/login", { email, password });
      setPartnerToken(r.data.token, r.data.agency_name);
      toast.success(`Welcome, ${r.data.agency_name}.`);
      nav("/partner/applications");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partner-login-page" className="min-h-screen bg-white text-[#0A0A0A] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/31167809/pexels-photo-31167809.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.55) grayscale(1)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <p className="label-overline">Ege Models · Partner Portal</p>
          <div>
            <h1 className="font-serif text-7xl tracking-tighter font-light leading-[0.9]">
              For booking<br /><span className="italic">agencies.</span>
            </h1>
            <p className="label-overline text-zinc-300 mt-6">Confidential access</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md">
          <p className="label-overline text-zinc-500">Sign in</p>
          <h2 className="font-serif text-5xl mt-4">Partner Portal.</h2>
          <p className="text-zinc-600 mt-3">
            Review incoming scouting applications and download comp cards.
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <Label className="label-overline text-zinc-600">Email</Label>
              <Input
                data-testid="partner-email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
              />
            </div>
            <div>
              <Label className="label-overline text-zinc-600">Password</Label>
              <Input
                data-testid="partner-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="partner-login-submit"
            className="btn-primary w-full justify-center mt-8"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />} Sign In
          </button>
          <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
            Accounts are issued individually by the studio. If you don&apos;t have credentials,
            reach out to <span className="text-black">info@egemodels.com</span>.
          </p>
        </form>
      </div>
    </div>
  );
}
